"use client";
import { useState } from "react";
import dynamic from "next/dynamic";
import { Send } from "lucide-react";

/**
 * `react-share` is loaded only when someone actually opens a share panel.
 *
 * It was a static import, and this component renders inside EVERY StickerCard
 * and DetailPackCard — so its 16 barrel imports rode into the shared client
 * bundle on every route. Measured, that put the chunk carrying react-share on
 * /about and /contact-us, pages with no share button anywhere on them.
 *
 * `ssr: false` because the panel only ever exists after a click: there is no
 * first paint to match, so server-rendering it would be pure waste. The button
 * itself — the only part visible before the click — stays in the main bundle,
 * so nothing about the card's initial render changes.
 */
const ShareButtons = dynamic(
  () => import("@/components/share/ShareButtons").then((m) => m.ShareButtons),
  {
    ssr: false,
    // Reserve one row of buttons while the chunk downloads. On a slow
    // connection the panel can arrive >500ms after the tap — past the
    // input-exclusion window — so without a placeholder the row popping in
    // would count as layout shift and push the card below it.
    loading: () => <div className="h-8 w-full" aria-hidden="true" />,
  },
);

interface CardShareToggleProps {
  url: string;
  title: string;
}

export function CardShareToggle({ url, title }: CardShareToggleProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label="Share sticker pack"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-ink hover:text-brand-600"
      >
        <Send className="h-6 w-6" strokeWidth={1.8} />
      </button>
      {open ? (
        <div className="mt-3 w-full">
          <ShareButtons url={url} title={title} />
        </div>
      ) : null}
    </>
  );
}
